import { Box, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useDebouncedResizeObserver from 'common/hooks/useDebouncedResizeObserver';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import useRegion from 'store/modules/app/hooks/useRegion';
import SpecialOffer from 'widgets/dialogs/SpecialOffer';
import {
  CountryAcknowledgement,
  FeaturedIn,
  HomeHeader,
  ListYourCar,
  MobileListYourCar,
  SectionContainer,
  TopBlogPosts,
} from 'widgets/home';
import CommonMetaTags from 'widgets/layout/CommonMetaTags';
import Footer from 'widgets/layout/Footer';
import HeaderNotification from 'widgets/layout/HeaderNotification';
import LinkTags from 'widgets/layout/LinkTags';
import OnlyDesktop from 'widgets/layout/Visibility/OnlyDesktop';
import OnlyMobile from 'widgets/layout/Visibility/OnlyMobile';
import OnlyTabletAndDesktop from 'widgets/layout/Visibility/OnlyTabletAndDesktop';

import NovatedLeasing from '../../widgets/home/NovatedLeasing';
import MobileNovatedLeasing from '../../widgets/home/NovatedLeasing/MobileNovatedLeasing';

import AllServices from './AllServices';
import FeaturedLocations from './FeaturedLocations';
import FeaturedModels from './FeaturedModels';
import { getFaqStructuredDataJson } from './getOrganizationStructuredDataJson';
import RenterFaq from './RenterFaq';
import Search from './Search';
import MobileSearch from './Search/MobileSearch';
import Slogan from './Slogan';
import TopHosts from './TopHosts';
import {
  getBackgroundImage,
  getCustomerEmail,
  getResetPasswordToken,
  getSpecialOfferFromLocation,
} from './utils';

const useStyles = makeStyles({
  rootDesktop: (props) => ({
    backgroundImage: `url(${props.bgImage})`,
    backgroundAttachment: 'fixed',
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat',
    [props.theme.breakpoints.down('md')]: {
      backgroundSize: 'contain',
      backgroundPosition: 'initial',
    },
  }),
  rootMobile: {
    backgroundImage: '#fff',
  },
  servicesSection: (props) => ({
    padding: '0px',
    backgroundColor: `${props.theme.palette.background.main}`,
  }),
  sloganSection: (props) => ({
    padding: props.theme.spacing(8, 0),
  }),
});

const HomePage = ({
  showSubscribe,
  location,
  onResetPasswordToken,
  onResetPassword,
  onSpecialOffer,
  onSignUp,
}) => {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { region } = useRegion();
  const { ref, width: bgWidth } = useDebouncedResizeObserver(10);
  const bgImage = getBackgroundImage(region);

  const classes = useStyles({ bgImage, bgWidth, theme });
  const root = isMobile ? classes.rootMobile : classes.rootDesktop;

  useEffect(() => {
    const processResetPassword = () => {
      const token = getResetPasswordToken(location);
      if (token) {
        onResetPasswordToken(token);
        onResetPassword();
      }
    };

    const processSpecialOffer = () => {
      const specialOffer = getSpecialOfferFromLocation(location);
      if (specialOffer) {
        onSpecialOffer(specialOffer);
      }
    };

    const processSignUp = () => {
      const email = getCustomerEmail(location);
      if (email) {
        onSignUp(email);
      }
    };

    processResetPassword();
    processSpecialOffer();
    processSignUp();
  }, [location, onSpecialOffer, onResetPassword, onResetPasswordToken]);

  return (
    <Box
      ref={ref}
      className={root}
      style={{
        backgroundPositionY: isTabletOrMobile || !bgWidth ? 0 : -Math.floor(0.42 * bgWidth - 504),
      }}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(getFaqStructuredDataJson())}
        </script>
      </Helmet>

      <LinkTags />

      <CommonMetaTags />

      {!isTabletOrMobile && <HeaderNotification />}

      <HomeHeader initiallyTransparent />

      <OnlyMobile>
        <SectionContainer>
          <MobileSearch />
        </SectionContainer>
      </OnlyMobile>

      <OnlyTabletAndDesktop>
        <Search />

        <SectionContainer className={classes.sloganSection}>
          <Slogan />
        </SectionContainer>
      </OnlyTabletAndDesktop>

      <SectionContainer>
        <FeaturedModels />
      </SectionContainer>

      <SectionContainer>
        <AllServices />
      </SectionContainer>

      {!isMobile ? (
        <SectionContainer>
          <NovatedLeasing />
        </SectionContainer>
      ) : (
        <SectionContainer backgroundColor="background.main">
          <MobileNovatedLeasing />
        </SectionContainer>
      )}

      <SectionContainer>
        <FeaturedLocations />
      </SectionContainer>

      <OnlyDesktop>
        <SectionContainer backgroundColor="background.main">
          <FeaturedIn />
        </SectionContainer>
      </OnlyDesktop>

      <SectionContainer
        className={classes.section}
        style={isMobile ? null : { paddingTop: theme.spacing(6) }}
      >
        <TopHosts />
      </SectionContainer>

      <SectionContainer>{isMobile ? <MobileListYourCar /> : <ListYourCar />}</SectionContainer>

      <SectionContainer>
        <TopBlogPosts />
      </SectionContainer>

      <SectionContainer>
        <RenterFaq />
      </SectionContainer>

      <CountryAcknowledgement />

      <SpecialOffer isMobile={isTabletOrMobile} />
      <Footer />
    </Box>
  );
};

HomePage.propTypes = {
  showSubscribe: PropTypes.bool,
  location: PropTypes.object,
  onResetPasswordToken: PropTypes.func,
  onResetPassword: PropTypes.func,
  onSpecialOffer: PropTypes.func,
  onSignUp: PropTypes.func.isRequired,
};

HomePage.defaultProps = {
  showSubscribe: false,
  location: {},
  onResetPasswordToken: () => {},
  onResetPassword: () => {},
  onSpecialOffer: () => {},
};

export default HomePage;
